import { saveOperationLogApi } from '@/services/Applog/ApplogService';

/**
 * 机构操作日志工具
 *
 * 模板解析机制说明：
 * 1. operationDesc 中使用 {{variable_name}} 格式定义模板变量
 * 2. extra 字段提供变量的实际值，格式为 { variable_name: actualValue }
 * 3. 展示时通过 operationLogParser.js 解析模板，将变量替换为实际值
 *
 * 示例：
 * - operationDesc: '审核驳回，驳回理由：{{rejection_reason}}'
 * - extra: { rejection_reason: '信息不完整' }
 * - 最终展示: '审核驳回，驳回理由：信息不完整'
 */

/**
 * 机构操作日志上报辅助函数
 * @param {Object} options - 日志参数
 * @param {string} options.entityId - 机构ID
 * @param {string} options.operationType - 操作类型: 'create', 'update', 'approve', 'reject', 'merge'
 * @param {string} options.operationDesc - 操作描述
 * @param {Object} options.extra - 扩展信息字段（可选）
 * @param {string} options.result - 操作结果: 'success' 或 'failed'
 * @param {Object} options.dataSnapshot - 数据快照（可选）
 * @returns {Promise} 返回操作结果
 */
export const logOrganizationOperation = async options => {
    const { entityId, operationType, operationDesc, extra = null, result = 'success', dataSnapshot = null } = options;

    // 构建日志数据
    const logData = {
        entityType: 'organization',
        entityId,
        module: 'organization',
        portal: 'boss', // 端口标识：用于区分同一个 module 在不同端口。
        operationType,
        operationDesc,
        extra: extra ? JSON.stringify(extra) : null,
        result,
        dataSnapshot: dataSnapshot ? JSON.stringify(dataSnapshot) : null,
        memcard: null,
    };

    try {
        const response = await saveOperationLogApi(logData);
        console.log('机构操作日志上报成功', logData);
        return response;
    } catch (error) {
        console.error('机构操作日志上报失败', error, logData);
        throw error;
    }
};

/**
 * 新增机构日志
 * @param {number} entityId - 机构ID
 * @param {Object} afterData - 新增后的机构信息
 * @param {boolean} isSuccess - 是否成功
 * @param {string} errorMessage - 失败时的错误信息
 */
export const logOrganizationCreate = async (entityId, afterData, isSuccess = true, errorMessage = null) => {
    const extra = isSuccess ? null : { error_message: errorMessage };
    const dataSnapshot = isSuccess ? { before: null, after: afterData } : null;

    return logOrganizationOperation({
        entityId,
        operationType: 'create',
        operationDesc: '新增机构',
        extra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot,
    });
};

/**
 * 编辑机构日志
 * @param {number} entityId - 机构ID
 * @param {Object} beforeData - 编辑前的机构信息
 * @param {Object} afterData - 编辑后的机构信息
 * @param {boolean} isSuccess - 是否成功
 * @param {string} errorMessage - 失败时的错误信息
 */
export const logOrganizationUpdate = async (entityId, beforeData, afterData, isSuccess = true, errorMessage = null) => {
    const extra = isSuccess ? null : { error_message: errorMessage };
    const dataSnapshot = isSuccess ? { before: beforeData, after: afterData } : null;

    return logOrganizationOperation({
        entityId,
        operationType: 'update',
        operationDesc: '编辑机构',
        extra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot,
    });
};

/**
 * 审核通过机构日志
 * @param {number} entityId - 机构ID
 */
export const logOrganizationApprove = async entityId => {
    return logOrganizationOperation({
        entityId,
        operationType: 'approve',
        operationDesc: '审核通过',
        result: 'success',
    });
};

/**
 * 审核驳回机构日志
 * @param {number} entityId - 机构ID
 * @param {string} rejectionReason - 驳回理由（可选，默认为"信息无效"）
 *
 * 模板变量说明：
 * - operationDesc: '审核驳回，驳回理由：{{rejection_reason}}'
 * - extra: { rejection_reason: rejectionReason }
 * - 展示时解析为: '审核驳回，驳回理由：具体的驳回理由'
 */
export const logOrganizationReject = async (entityId, rejectionReason = '信息无效') => {
    return logOrganizationOperation({
        entityId,
        operationType: 'reject',
        operationDesc: '审核驳回，驳回理由：{{rejection_reason}}',
        extra: { rejection_reason: rejectionReason },
        result: 'success',
    });
};

/**
 * 合并机构日志
 * @param {number} mainEntityId - 主机构ID（保留的机构）
 * @param {Array} mergedOrgs - 被合并的机构列表，格式：[{id, name}, ...]
 *
 * 模板变量说明：
 * - operationDesc: '合并机构，{{merged_orgs}}被合并至本机构'
 * - extra: { merged_orgs: '机构1 (#123)、机构2 (#456)' }
 * - 展示时解析为: '合并机构，机构1 (#123)、机构2 (#456)被合并至本机构'
 */
export const logOrganizationMerge = async (mainEntityId, mergedOrgs) => {
    // 组装被合并机构信息
    const mergedOrgNames = mergedOrgs.map(org => `${org.name} (#${org.id})`).join('、');

    return logOrganizationOperation({
        entityId: mainEntityId,
        operationType: 'merge',
        operationDesc: '合并机构，{{merged_orgs}}被合并至本机构',
        extra: { merged_orgs: mergedOrgNames },
        result: 'success',
    });
};

/**
 * 撤销合并机构日志
 * @param {number} entityId - 机构ID
 * @param {Object} mainOrg - 主机构信息，格式：{id, name}
 *
 * 模板变量说明：
 * - operationDesc: '撤销合并，主机构：{{main_org}}'
 * - extra: { main_org: '主机构名 (#12234)' }
 * - 展示时解析为: '撤销合并，主机构：主机构名 (#12234)'
 */
export const logOrganizationUnmerge = async (entityId, mainOrg) => {
    const mainOrgName = `${mainOrg.name} (#${mainOrg.id})`;

    return logOrganizationOperation({
        entityId,
        operationType: 'unmerge',
        operationDesc: '撤销合并，主机构：{{main_org}}',
        extra: { main_org: mainOrgName },
        result: 'success',
    });
};
